<template>
  <v-card class="elevation-0">
    <v-card-title>
      <v-text-field v-model="search" append-icon="search" label="Search" single-line hide-details></v-text-field>
    </v-card-title>
    <v-data-table :headers="headers" :items="releaseNoteList" :search="search">
      <template v-slot:item="{ item }">
        <tr>
          <td style="width: 20px;">
            <v-btn data-cy="documentationStatusList__editIcon" icon small @click="edit(item.id)">
              <v-icon small color="grey darken-2">fas fa-edit</v-icon>
            </v-btn>
          </td>
          <td class="och-myReleaseNotesTable__cell--title">
            <router-link :to="`/pack/${packId}/releaseNote/${item.id}/view`">
              {{ item.title }}
            </router-link>
          </td>
          <td>
            <v-tooltip v-if="item.summary" bottom max-width="420">
              <template v-slot:activator="{ on, attrs }">
                <div class="och-myReleaseNotesTable__cell--summary" v-bind="attrs" v-on="on">{{ truncate(stripHtmlTags(item.summary)) }}</div>
              </template>
              <span v-html="item.summary"></span>
            </v-tooltip>
          </td>
          <td>{{ item.buildType }}</td>
          <td>{{ item.application }}</td>
          <td>{{ item.primaryActivity }}</td>
          <td>{{ formatArray(item.userRole) }}</td>
          <td>{{ item.releaseType }}</td>
          <td>{{ item.qaAnalystDisplayName || "--" }}</td>
          <td>{{ item.qaJira }}</td>
          <td>{{ item.buildAnalystDisplayName || "--" }}</td>
        </tr>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import releaseNoteApi from "@/api/releaseNote";
import { formatArray } from "@/lib/formatters";
import { truncate, stripHtmlTags } from "@/lib/summaryFormatters";
import { mapActions, mapGetters } from "vuex";

export default {
  computed: {
    theme() {
      return this.$store.state.session.theme;
    },
    roleName() {
      return this.$store.state.session.user.roleName;
    },
    username() {
      return this.$store.state.session.username;
    },
    ...mapGetters({
      userMap: "user/getUserMap",
    }),
  },
  async created() {
    try {
      await Promise.all([this.getReleaseNoteList(), this.fetchUserList()]);
    } catch (e) {
      console.error(e);
      this.$notifyError({ message: "Unable to fetch release note list" });
    }
  },
  data() {
    return {
      formatArray,
      headers: [
        {
          text: "",
          align: "left",
          sortable: false,
        },
        {
          text: "Title",
          align: "left",
          sortable: true,
          value: "title",
        },
        {
          text: "Summary",
          align: "left",
          sortable: true,
          value: "summary",
        },
        {
          text: "Build Type",
          align: "left",
          sortable: true,
          value: "buildType",
        },
        {
          text: "Application",
          align: "left",
          sortable: true,
          value: "application",
        },
        {
          text: "Primary Activity",
          align: "left",
          sortable: true,
          value: "primaryActivity",
        },
        {
          text: "User Role",
          align: "left",
          sortable: true,
          value: "userRole",
        },
        {
          text: "Release Type",
          align: "left",
          sortable: true,
          value: "releaseType",
        },
        {
          text: "QA Analyst",
          align: "left",
          sortable: true,
          value: "qaAnalyst",
        },
        {
          text: "QA JIRA",
          align: "left",
          sortable: true,
          value: "qaJira",
        },
        {
          text: "Build Analyst",
          align: "left",
          sortable: true,
          value: "buildAnalyst",
        },
      ],
      releaseNoteList: [],
      search: null,
    };
  },
  methods: {
    edit(releaseNoteId) {
      this.$router.push({ name: "releaseNoteEdit", params: { releaseNoteId, packId: this.packId } });
    },
    truncate,
    stripHtmlTags,
    async getReleaseNoteList() {
      const documentationStatusFilter = {
        attributeName: "documentationStatus",
        attributeNameLabel: "Documentation Status",
        dataType: "string",
        operator: "in",
        operatorLabel: "=",
        value: [this.status],
        valueLabel: this.status,
        valueType: "array",
      };

      let analystFilter;
      if (this.roleName === "ochinBiAnalyst") {
        analystFilter = {
          attributeName: "jiraSupportAnalyst",
          attributeNameLabel: "JIRA Support Analyst",
          dataType: "string",
          operator: "in",
          operatorLabel: "=",
          value: [this.username],
          valueLabel: "JIRA Support Analyst",
          valueType: "array",
        };
      } else {
        analystFilter = {
          attributeName: "qaAnalyst",
          attributeNameLabel: "QA Analyst",
          dataType: "string",
          operator: "in",
          operatorLabel: "=",
          value: [this.username],
          valueLabel: "QA Analyst",
          valueType: "array",
        };
      }

      let result;
      try {
        result = await releaseNoteApi.packDashboardWidgetSearch({
          filterList: [documentationStatusFilter, analystFilter],
          packId: this.packId,
        });
        this.releaseNoteList = result;
      } catch (e) {
        console.error(e);
        this.$notifyError("Could not refresh list of release notes.");
      }
    },
    goToReleaseNote(packId, releaseNoteId) {
      this.$router.push({
        name: "releaseNoteView",
        params: { packId, releaseNoteId },
      });
    },
    ...mapActions({
      fetchUserList: "user/load",
    }),
  },
  props: ["packId", "status", "title"],
  watch: {
    packId() {
      this.getReleaseNoteList();
    },
    releaseNoteList(val) {
      this.$emit("noteCount", val.length);
    },
  },
};
</script>

<style>
.och-myReleaseNotesTable__cell--summary {
  max-width: 20rem;
  min-width: 15rem;
  overflow: hidden;
}

.och-myReleaseNotesTable__cell--title {
  min-width: 15rem;
}

.och-myReleaseNotesTable__tooltip {
  font-size: larger;
}
</style>
